import { map, take, takeUntil } from 'rxjs/operators';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { AbstractField } from '../../models/field-configs/abstract-field';
import { FieldDateInputConfig } from './models/field-date-input.config';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { getContractSelectData } from '../../../../../order-builder/modules/specification/selectors/contract.selector';
import { WeekDay } from './models/week-day';
import { BaseAsyncValue, getObservableFromAsyncValueSafe } from '../../../../models/async-value';
import { getLanguage, State } from '@cemex/cmx-client-layout';
import { DateOptionLoaderI } from './models/select-option-loader-i';
import { ORDER_BUILDER_AVAILABLE_DAYS_FROM_MIN } from '@ordersnproducts/modules/shared/constants/order-defaults';
// @ts-ignore
import { DatepickerInputTags } from '@cemex-core/cmx-api';

import * as moment from 'moment';
import { StatusMessageParameter } from '../../models/field-configs/field-config.interface';

@Component({
  selector: 'form-input',
  styleUrls: ['form-input-date.component.scss'],
  templateUrl: 'form-input-date.component.html',
})
export class FormInputDateComponent extends AbstractField implements OnInit, OnDestroy {
  config: FieldDateInputConfig;
  group: UntypedFormGroup;
  disabledDates = [];
  fieldControl: AbstractControl;
  languageStore: string;
  startWeekWith: WeekDay = WeekDay.SUNDAY;
  controlStatus$ = new BehaviorSubject('regular');
  controlStatusMessage$ = new BehaviorSubject('');
  public statusMessage: string;
  public statusMessageParameter: StatusMessageParameter;

  forbiddenWeekDays: WeekDay[];

  min: Date = new Date();
  max: Date = new Date();
  availableDaysFromMin = ORDER_BUILDER_AVAILABLE_DAYS_FROM_MIN;

  singleDateSelection = true;
  tags: Observable<DatepickerInputTags>;
  isRequired = false;

  buttonless = false;
  successLabel: string;

  disabled = false;

  private optionsLoader: DateOptionLoaderI;
  private _ngDestroyed = new Subject();

  ngOnInit(): void {
    this.getLanguage();
    this.fieldControl = this.getControl();
    this.setDisabledDates();

    this.store.select(getContractSelectData).subscribe((contractState) => {
      if (contractState.masterContract.selectedContract && contractState.masterContract.selectedDate) {
        this.fieldControl.setValue(contractState.masterContract.selectedDate.date);
      } else {
        this.setDefaultDateValue();
      }
    });

    if (this.config.min instanceof Date) {
      this.min = this.config.min;
    } else if (this.config.min instanceof BaseAsyncValue) {
      this.config.min
        .getValue()
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((date) => {
          this.min = date;
          if (this.fieldControl.value && this.fieldControl.value < this.min) {
            this.fieldControl.setValue(this.min);
          }
        });
    }

    if (this.config.presetDate) {
      getObservableFromAsyncValueSafe(this.config.presetDate)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((presetDate) => {
          this.fieldControl.setValue(presetDate);
        });
    }

    if (this.config.max) {
      getObservableFromAsyncValueSafe(this.config.max)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((date) => {
          this.max = date;
        });
    }

    if (this.config.blocked) {
      getObservableFromAsyncValueSafe(this.config.blocked)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((blocked) => {
          this.disabled = blocked;
        });
    }

    // Custom loader so you don't have to create new component
    // each time you want to preload options from API
    if (this.config.optionsLoader !== undefined) {
      this.optionsLoader = this.injector.get<DateOptionLoaderI>(this.config.optionsLoader);
      this.optionsLoader
        .loadOptions(this.group, this.config)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((options) => {
          if (options) {
            this.min = options.min;
            this.max = options.max;

            if (this.min && (this.fieldControl.value < this.min || !this.fieldControl.value)) {
              this.fieldControl.setValue(this.min);
            }
          }
        });
    }

    if (this.config.singleDateSelection) {
      this.singleDateSelection = this.config.singleDateSelection;
    }

    if (this.config.tags) {
      this.tags = of(this.config.tags);
    }

    if (this.config.hasOwnProperty('startWeekWith')) {
      this.startWeekWith = this.config.startWeekWith;
    }

    if (this.config.hasOwnProperty('forbiddenWeekDays')) {
      this.forbiddenWeekDays = this.config.forbiddenWeekDays;
    }

    if (this.config.hasOwnProperty('successLabel')) {
      this.successLabel = this.config.successLabel;
    }

    if (this.config.hasOwnProperty('hideOutsideDays')) {
      this.config.hideOutsideDays = true;
    }

    if (this.config.hasOwnProperty('availableDaysFromMin')) {
      if (this.config.availableDaysFromMin instanceof BaseAsyncValue) {
        this.config.availableDaysFromMin
          .getValue()
          .pipe(takeUntil(this._ngDestroyed))
          .subscribe((maxDay) => {
            this.availableDaysFromMin = !!maxDay ? maxDay : this.availableDaysFromMin;
            this.max = moment(this.min).add(this.availableDaysFromMin, 'days').toDate();
          });
      } else {
        this.availableDaysFromMin = this.config.availableDaysFromMin as number;
        this.max = moment(this.min).add(this.availableDaysFromMin, 'days').toDate();
      }
    } else if (!this.config.max) {
      this.max = moment(this.min).add(this.availableDaysFromMin, 'days').toDate();
    }
    if (this.config.disabledDates) {
      getObservableFromAsyncValueSafe(this.config.disabledDates)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((disabledDates) => {
          this.disabledDates = [...this.disabledDates, ...disabledDates];
        });
    }

    this.tags = this.translateService
      .stream([
        this.config.translationNames.placeholder,
        this.config.translationNames.cancel,
        this.config.translationNames.apply,
        this.config.translationNames.months,
        this.config.translationNames.monthsShort,
        this.config.translationNames.daysShort,
        this.config.translationNames.daysLong,
        this.config.translationNames.daysMin,
        this.config.translationNames.momentFormat,
      ])
      .pipe(
        map((keys) => {
          return new DatepickerInputTags(
            keys[this.config.translationNames.placeholder],
            keys[this.config.translationNames.cancel],
            keys[this.config.translationNames.apply],
            keys[this.config.translationNames.months].split(','),
            keys[this.config.translationNames.monthsShort].split(','),
            keys[this.config.translationNames.daysShort].split(','),
            keys[this.config.translationNames.daysLong].split(','),
            keys[this.config.translationNames.daysMin].split(','),
            keys[this.config.translationNames.momentFormat],
          );
        }),
      );

    this.isRequired = this.hasRequiredField(this.fieldControl);

    if (this.config.statusMessageParameter) {
      getObservableFromAsyncValueSafe(this.config.statusMessageParameter)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((statusMessageParameter) => {
          this.statusMessageParameter = statusMessageParameter;
        });
    }

    if (this.config.statusMessage) {
      getObservableFromAsyncValueSafe(this.config.statusMessage)
        .pipe(takeUntil(this._ngDestroyed))
        .subscribe((statusMessage) => {
          this.statusMessage = statusMessage;
          this.controlStatusMessage$.next(statusMessage);
        });
    }

    this.fieldControl.statusChanges
      .pipe(
        map((statusMessage) => {
          if (this.fieldControl.invalid && this.fieldControl.hasError('required') && !this.fieldControl.untouched) {
            this.controlStatusMessage$.next('forms.required');
            this.controlStatus$.next('error');
          } else if (this.successLabel && !this.fieldControl.invalid) {
            this.controlStatusMessage$.next(this.successLabel);
            this.controlStatus$.next('success');
          } else if (this.fieldControl.invalid && this.fieldControl.hasError('warning')) {
            this.controlStatusMessage$.next('');
            this.controlStatus$.next('warning');
          } else if (this.config.statusMessage) {
            this.controlStatus$.next('regular');
            this.controlStatusMessage$.next(this.statusMessage);
          } else {
            this.controlStatusMessage$.next('');
            this.controlStatus$.next('regular');
          }
          return statusMessage;
        }),
        takeUntil(this._ngDestroyed),
      )
      .subscribe();
  }

  getLanguage() {
    this.store.select(getLanguage).subscribe((languge: string) => {
      this.languageStore = languge;
    });
  }

  /**
   * If the fieldControl has value from previous setup, don't do anything
   * Else use the the value set in the init config
   * Otherwise use current Date
   */
  setDefaultDateValue(): void {
    if (this.fieldControl.value instanceof Date) {
      return;
    }

    if (this.config.emptyDate) {
      return;
    }

    /**
     * Set min date as value if config.value is not defined
     * If both are not defined set new Date()
     *
     * If value is defined, solve it,
     * If its not, try to solve min value and set as default value
     * If both of them are not defined, just set current date
     */
    if (this.config.value) {
      // config.value is defined, solve it
      if (this.config.value instanceof Date) {
        this.fieldControl.setValue(this.config.value);
      } else if (this.config.value instanceof BaseAsyncValue) {
        this.config.value
          .getValue()
          .pipe(take(1))
          .subscribe((date) => {
            setTimeout(() => {
              this.fieldControl.setValue(date);
            });
          });
      }
    } else if (this.config.min) {
      // config.value is not defined, try to set the config.min date as value
      if (this.config.min instanceof Date) {
        this.fieldControl.setValue(this.config.min);
      } else if (this.config.min instanceof BaseAsyncValue) {
        this.config.min
          .getValue()
          .pipe(take(1))
          .subscribe((date) => {
            if (!this.fieldControl.value) {
              setTimeout(() => {
                this.fieldControl.setValue(date);
              });
            }
          });
      }
    } else {
      // None of them were defined. Set current date
      this.fieldControl.setValue(new Date());
    }
  }
  private setDisabledDates() {
    const disableFromWeekDays = (date, key) => {
      return date.getDay() === key;
    };
    if (this.config.hasOwnProperty('forbiddenWeekDays')) {
      this.config.forbiddenWeekDays.forEach((singleDate) => {
        this.disabledDates.push((date) => disableFromWeekDays(date, singleDate));
      });
    }
  }

  constructor(
    private translateService: TranslateService,
    public store: Store<State>,
    private injector: Injector,
  ) {
    super();
  }

  ngOnDestroy(): void {
    this._ngDestroyed.next();
    this._ngDestroyed.complete();
  }
}
